<template>
  <div class="add-extra-info">
    <ValidationObserver
      v-slot="{handleSubmit}"
      slim>
      <form
        @submit.prevent="handleSubmit(submit)"
        :class="['d-flex', 'flex-column', 'justify-content-between', (submitting || disabled) ? 'disabled' : '']"
        method="post">
        <div>
          <TextField
            :field-name="$t('name')"
            :placeholder="$t('ip.registration.name.placeholder')"
            :disabled="disabled || submitting"
            v-model="user.name"
            :rules="{required: true, max: 40}"/>
          <TextField
            :disabled="disabled || submitting"
            v-model="user.mobile_phone"
            :field-name="$t('mobile.number')"
            :placeholder="$t('mobile.number.placeholder')"
            :rules="{required: true, min:8, max: 20}"/>
          <SimpleMultiSelectField
            :disabled="disabled || submitting"
            v-if="Array.isArray(company.departments) && company.departments.length > 0"
            :placeholder="$t('department')"
            :field-name="$t('department')"
            :rules="{required: true}"
            v-model="user.department"
            :options="company.departments"/>
          <DateOfBirthField
            v-if="company.for_student"
            v-model="user.birth_date"
            :rules="{ required: true, date: true,min_age:16, }"
            :field-name="$t('birth.date')"
            :disabled="disabled || submitting"
            :format="serverAcceptedFormat"/>
          <TextField
            :disabled="disabled || submitting"
            v-if="company.employee_number"
            v-model="user.employee_number"
            :field-name="$t('employee.number')"
            :placeholder="$t('employee.number.placeholder')"
            :rules="{required: true}"/>
          <SimpleMultiSelectField
            :disabled="disabled || submitting"
            v-if="company.for_student"
            :placeholder="$t('register.are.you.student')"
            :rules="{required: true}"
            :field-name="`${$t('graduate.student')}?`"
            v-model="user.senior_student"
            :options="company.senior_students"/>
          <SimpleMultiSelectField
            :disabled="disabled || submitting"
            v-if="company.for_student"
            :placeholder="$t('register.what.is.your.study.field')"
            :field-name="$t('study.field')"
            :rules="{required: true}"
            v-model="user.field_of_study"
            :options="company.study_fields"/>
          <transition
            name="expand"
            mode="out-in">
            <TextField
              :disabled="disabled || submitting"
              v-if="user.ip_plus && company.has_registration_code"
              v-model="user.ip_plus_code"
              :field-name="$t('register.enter.code')"
              :rules="{required: true}"/>
          </transition>
          <CheckboxField
            :disabled="disabled || submitting"
            :field-name="$t('terms.conditions')"
            v-model="user.agreement_accepted"
            :rules="{required: true}"
            :text="ipRegTerms"/>
        </div>
        <div>
          <Confirmation
            v-if="!success && showConfirmBox"
            :success="success"
            :message="confirmMsg.message"
            :title="confirmMsg.title"
            class="pb-2 mb-6"/>
          <LoadingButton
            :disabled="submitting || disabled"
            :loading="submitting"
            button-class="btn-standard mt-0">
            {{ loggedIn ? $t('register.button.text.logged.in') : $t('register.button.text') }}
          </LoadingButton>
          <button
            :disabled="disabled || submitting"
            @click="$emit('back')"
            type="button"
            class="btn-secondary mt-3">
            {{ $t('go.back') }}
          </button>
        </div>
      </form>
    </ValidationObserver>
  </div>
</template>

<script>
import { ValidationObserver } from 'vee-validate';
import Confirmation from '@components/elements/Confirmation';
import LoadingButton from '@components/elements/LoadingButton';
import { ajaxForm, corporateIp, formDescriptionHander, userInfo } from '@mixins';
import TextField from '@components/elements/TextField';
import SimpleMultiSelectField from '@components/elements/SimpleMultiSelectField';
import Utils from '@root/src/helpers/utils';
import CheckboxField from '@components/elements/CheckboxField';
import deepmerge from 'deepmerge';
import DateOfBirthField from '@elements/DobField';
import { addTrackingEvent } from '@root/src/helpers/Tracking';
import {
  analyticsInfoString,
  normalizeToLowerSnakeCase,
} from '@root/src/helpers/trackingLabels';
import { UPDATE_IP_LOGIN_COMPANY_ANALYTICS_INFO } from '@store';

export default {
  name: 'add-extra-info',
  components: {
    CheckboxField,
    SimpleMultiSelectField,
    TextField,
    ValidationObserver,
    Confirmation,
    LoadingButton,
    DateOfBirthField,
  },
  mixins: [ajaxForm, corporateIp, userInfo, formDescriptionHander],
  data() {
    return {
      dateFormat: Utils.getDefaultDateFormat(),
      serverAcceptedFormat: 'DD-MM-YYYY',
      token: '',
      user: {
        name: '',
        mobile_phone: '',
        department: '',
        ip_plus: false,
        ip_plus_code: '',
        product: '',
        date_of_birth: '',
        employee_number: '',
        senior_student: '',
        field_of_study: '',
        agreement_accepted: false,
      }
    };
  },
  computed: {
    company() {
      return this.$store.state.corporate.company;
    },
    ipRegTerms() {
      if (this.company.terms_n_conditions) {
        return this.$t('ip.registration.additional.terms.conditions', { name: this.capitalize(this.company.sponsor) });
      }
      return this.$t('ip.registration.terms.conditions');
    },
  },
  watch: {
    'company.ip_plus': {
      handler(newValue) {
        this.user.ip_plus = newValue;
      },
      immediate: true
    }
  },
  mounted() {
    this.token = this.$route.query.token || '';
  },
  methods: {
    customFormDescriptionHandler() {
      if (isServer) {
        return false;
      }
      this.formHeading = this.$t('ip.registration.page.title');
      this.formSubHeading = '';
      return true;
    },
    successWorkflow() {
      const response = this.serverResponseMessage || {};
      const ipInfo = response.analytics_info || {};
      this.$authWebapp.pushAnalyticsEvent(ipInfo);
      this.$store.commit(`corporate/${UPDATE_IP_LOGIN_COMPANY_ANALYTICS_INFO}`, ipInfo);
      const company = this.company || {};
      addTrackingEvent({
        event: 'system_action',
        sub_producer_contract: { name: 'ip_login', version: '1' },
        attributes: {
          action: 'success',
          identifier_type: 'ip_login_step',
          identifier_value: normalizeToLowerSnakeCase('Add Extra Info'),
          registration_service:
            analyticsInfoString(
              ipInfo,
              'registrationService',
              'registration_service'
            ) || 'ip_login',
          paywall_variation:
            analyticsInfoString(
              ipInfo,
              'paywallVariation',
              'paywall_variation'
            ) || this.$route.query.paywall_variation_type || '',
          flow_source_url:
            analyticsInfoString(ipInfo, 'flowSourceUrl', 'flow_source_url') ||
            this.$authWebapp.getTarget('') ||
            '',
          product_code: analyticsInfoString(
            ipInfo,
            'productCode',
            'product_code',
            'SKU'
          ),
          product_text: analyticsInfoString(
            ipInfo,
            'productText',
            'product_text',
            'subscriptionName'
          ),
          marketing_activity_code: analyticsInfoString(
            ipInfo,
            'marketingActivityCode',
            'marketActivityCode',
            'marketing_activity_code'
          ),
          ip_company_name:
            analyticsInfoString(
              ipInfo,
              'ipCompanyName',
              'ip_company_name',
              'corporateCustomerName'
            ) ||
            company.name ||
            '',
          ip_company_agreement_id:
            analyticsInfoString(
              ipInfo,
              'ipCompanyAgreementId',
              'ip_company_agreement_id',
              'agreement_id'
            ) ||
            company.agreement_id ||
            '',
        },
      });
      this.$emit('success');
    },
    errorWorkflow() {
    },
    submit() {
      const app = this;
      const data = deepmerge({}, this.user);
      data.username = this.username;
      if (this.token) {
        data.token = app.$route.query.token;
      }
      const slug = this.$route.params.slug || '';
      app.submitForm(() => app.subscriptionClient.registerForIpSubscription(slug, data), true);
    }
  }
};
</script>
